import React, { useCallback, useMemo, useState } from "react"
import { useRouter } from "next/router"

import {
  Badge,
  Button,
  ChevronDownIcon,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SearchInput,
} from "@coval/ui"
import { Bot, Plus } from "lucide-react"

import { useQueryParams } from "@/hooks/useQueryParams"
import { formatSimulatorType } from "@/utils/formatting/format-simulator-type"

import { useAgentFilter } from "../hooks/useAgentFilter"

interface AgentFilterSelectorProps {
  className?: string
}

export const AgentFilterSelector: React.FC<AgentFilterSelectorProps> = ({
  className,
}) => {
  const { agentId, agentData, agents, setAgentFilter, clearAgentFilter } =
    useAgentFilter()
  const router = useRouter()
  const queryParams = useQueryParams()
  const [searchQuery, setSearchQuery] = useState("")
  const [open, setOpen] = useState(false)

  const filteredAgents = useMemo(() => {
    if (!searchQuery) {
      return agents
    }
    const lower = searchQuery.toLowerCase()
    return agents.filter(
      (agent) =>
        agent.display_name.toLowerCase().includes(lower) ||
        formatSimulatorType(agent.model_type).toLowerCase().includes(lower),
    )
  }, [agents, searchQuery])

  const getButtonText = () => {
    if (!agentData) {
      return "All Agents"
    }
    return agentData.display_name
  }

  const handleAgentSelect = (key: string) => {
    if (key === "all") {
      clearAgentFilter()
    } else {
      setAgentFilter(key)
      const slug = router.query.slug as string | undefined
      void router.push(
        queryParams.getHref(
          `/agents/${encodeURIComponent(key)}?agent_id=${encodeURIComponent(key)}`,
          slug,
        ),
      )
    }
    setSearchQuery("")
    setOpen(false)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setSearchQuery("")
    }
  }

  const handleAddAgent = useCallback(() => {
    const slug = router.query.slug as string | undefined
    queryParams.push("/agents", slug, { query: { agent_id: null } })
    setSearchQuery("")
    setOpen(false)
  }, [queryParams, router])

  return (
    <div className={cn("", className)}>
      <DropdownMenu open={open} onOpenChange={handleOpenChange}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            className="w-full h-[28px]! max-h-[28px]! justify-start gap-2 px-2"
            aria-label="Filter by agent"
          >
            <Bot className="w-4 h-4 shrink-0" />
            <span className="truncate flex-1 text-left">{getButtonText()}</span>
            <ChevronDownIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="flex min-w-[250px] max-h-[50vh] flex-col"
          align="start"
        >
          <div className="shrink-0 p-2">
            <SearchInput
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onClear={() => setSearchQuery("")}
              placeholder="Search..."
              aria-label="Search agents"
              autoFocus
              onKeyDown={(event) => {
                if (event.key !== "Escape") {
                  event.stopPropagation()
                }
              }}
            />
          </div>
          <DropdownMenuSeparator className="shrink-0" />
          <div className="overflow-y-auto">
            <DropdownMenuItem
              className="py-2"
              onSelect={() => handleAgentSelect("all")}
            >
              <div className="flex items-center justify-between gap-2 w-full">
                <div className="flex-1 min-w-0">
                  <div className="font-medium">All Agents</div>
                  <div className="text-xs text-coval-text-muted">
                    Show data from all agents
                  </div>
                </div>
                {!agentId && (
                  <Badge variant="contrast" size="status">
                    Active
                  </Badge>
                )}
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {filteredAgents.length === 0 ? (
              <DropdownMenuItem disabled>
                <span className="text-coval-text-muted">
                  {searchQuery ? "No agents found" : "No agents available"}
                </span>
              </DropdownMenuItem>
            ) : (
              filteredAgents.map((agent) => (
                <DropdownMenuItem
                  key={agent.id}
                  className="py-2"
                  onSelect={() => handleAgentSelect(agent.id)}
                >
                  <div className="flex items-center justify-between gap-2 w-full">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {agent.display_name}
                      </div>
                      <div className="text-xs text-coval-text-muted">
                        {formatSimulatorType(agent.model_type)}
                      </div>
                    </div>
                    {agent.id === agentId && (
                      <Badge variant="contrast" size="status">
                        Active
                      </Badge>
                    )}
                  </div>
                </DropdownMenuItem>
              ))
            )}
          </div>
          <DropdownMenuSeparator className="shrink-0" />
          <DropdownMenuItem className="shrink-0 py-2" onSelect={handleAddAgent}>
            <div className="flex items-center gap-2 w-full">
              <Plus className="w-4 h-4 shrink-0" />
              <span className="font-medium">Add agent</span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
